import * as React from 'react';
import { compose, setDisplayName, withHandlers, withProps, withState, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { State } from 'reducers';
import { translate } from 'react-i18next';
import { OemProps, skin } from 'ui/styles/skin';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { FadeCollapse } from 'ui/transitions/FadeCollapse';
import { generateFileName } from 'logic/reports/generateFileName';
import { UtcTime, ReportId, ReportType } from 'types';
import { getTimezoneOffsetCalculator, getReportsPath } from 'selectors/serverSelectors';
import { CustomFileNameFormatLegend } from './CustomFileNameFormatLegend';
import { Input } from 'ui/shared/Input';
import { hasFeature } from 'logic/reports/reportType';
import { DistributionOption } from './DistributionOption';
import { Required } from 'ui/pages/designer/Required';

import './FileDistribution.css';

export type OwnProps = {
    reportId: ReportId;
    reportType: ReportType;
    reportName: string;
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const labelStyle = {
    marginTop: '1em',
};
const relativeStyle: React.CSSProperties = {
    position: 'relative',
};

const RawFileDistribution = ({
    reportType,
    reportSettings,
    fileNameFormat,
    path,
    csvDelim,
    onChange,
    fileTypes,
    handleChangePath,
    handleChangeFileName,
    handleChangeCSVType,
    handleBlurCSV,
    handleChangeUseCustom,
    handleChangeUseUtc,
    handleChangeFileType,
    exampleFileName,
    handleFocusFileName,
    handleBlurFileName,
    handleBlurPath,
    legendVisible,
    t,
    styles,
}: Props) =>
    <div id="edit-report-file-distribution">
        <div style={ labelStyle }>
            <label htmlFor="edit-report-file-path">
                { t('Server_Path') }
            </label>
        </div>
        <div>
            <Input
                id="edit-report-file-path"
                value={ path }
                onChange={ handleChangePath }
                onBlur={ handleBlurPath }
            />
        </div>
        <div style={ labelStyle }>
            <label>
                <input
                    type="checkbox"
                    id="edit-report-file-use-custom"
                    checked={ !reportSettings.useDefaultFileName }
                    onChange={ handleChangeUseCustom }
                />
                { t('WC_REPORT_CUSTOM_FILE_NAME') }
            </label>
        </div>
        <FadeCollapse
            visible={ !reportSettings.useDefaultFileName }
            duration={ 200 }
            height="4em"
        >
            <div>
                <label htmlFor="edit-report-file-name">
                    { t('WC_FILE_NAME') }
                </label>
            </div>
            <span style={ relativeStyle }>
                <Input
                    id="edit-report-file-name"
                    value={ fileNameFormat }
                    onChange={ handleChangeFileName }
                    disabled={ reportSettings.useDefaultFileName }
                    onFocus={ handleFocusFileName }
                    onBlur={ handleBlurFileName }
                />
                <CustomFileNameFormatLegend
                    visible={ legendVisible }
                />
            </span>
        </FadeCollapse>
        <div>
            <label>
                <input
                    type="checkbox"
                    id="edit-report-file-use-utc"
                    checked={ reportSettings.useUtcInFileName }
                    onChange={ handleChangeUseUtc }
                />
                { t('Reports_Settings_Distribution_UseUTC') }
            </label>
        </div>
        <div id="edit-report-example-file-name" style={ styles.Bordered }>
            <label style={ styles.Workspace }>{ t('EXAMPLE_LABEL') }</label>
            <span>{ exampleFileName }</span>
        </div>
        <div style={ labelStyle } className="inline-child-divs">
            <label>
                { t('WC_REPORT_FILE_FORMATS') }
            </label>
            <Required visible={ Object.keys(fileTypes).reduce((v, ft) => v && !fileTypes[ft], true) }/>
        </div>
        <DistributionOption
            method="file"
            type="PDF"
            textKey="6.1241.0.36"
            fileTypes={ fileTypes }
            csvDelim={ csvDelim }
            onChange={ handleChangeFileType }
            onCSVChange={ handleChangeCSVType }
            onCSVBlur={ handleBlurCSV }
        />
        <DistributionOption
            method="file"
            type="CSV"
            textKey="6.1241.0.35"
            fileTypes={ fileTypes }
            csvDelim={ csvDelim }
            onChange={ handleChangeFileType }
            onCSVChange={ handleChangeCSVType }
            onCSVBlur={ handleBlurCSV }
        />
        <DistributionOption
            method="file"
            type="XLS"
            textKey="6.1241.0.34"
            fileTypes={ fileTypes }
            csvDelim={ csvDelim }
            onChange={ handleChangeFileType }
            onCSVChange={ handleChangeCSVType }
            onCSVBlur={ handleBlurCSV }
        />
        <DistributionOption
            method="file"
            type="XML"
            textKey="Extensible_Markup_Language"
            fileTypes={ fileTypes }
            csvDelim={ csvDelim }
            onChange={ handleChangeFileType }
            onCSVChange={ handleChangeCSVType }
            onCSVBlur={ handleBlurCSV }
        />
        { hasFeature(reportType, 'hasHeatMap') &&
            <DistributionOption
                method="file"
                type="Image"
                textKey="1613"
                fileTypes={ fileTypes }
                csvDelim={ csvDelim }
                onChange={ handleChangeFileType }
                onCSVChange={ handleChangeCSVType }
                onCSVBlur={ handleBlurCSV }
            />
        }
        { hasFeature(reportType, 'hasSummary') &&
            <DistributionOption
                method="file"
                type="Summary"
                textKey="IncludeSummaryInformation"
                fileTypes={ fileTypes }
                csvDelim={ csvDelim }
                onChange={ handleChangeFileType }
                onCSVChange={ handleChangeCSVType }
                onCSVBlur={ handleBlurCSV }
            />
        }
    </div>
;

type ChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;
type FocusHandler = (e: React.FocusEvent<HTMLInputElement>) => void;

type StateProps = {
    tzOffsetCalculator: (t: UtcTime) => number;
    defaultReportsPath: string;
};
type RecomposeProps = {
    fileNameFormat: string;
    path: string;
    csvDelim: string;
    handleChangePath: ChangeHandler;
    handleChangeFileName: ChangeHandler;
    handleChangeUseCustom: ChangeHandler;
    handleChangeUseUtc: ChangeHandler;
    handleChangeFileType: ChangeHandler;
    handleChangeCSVType: ChangeHandler;
    handleBlurCSV: FocusHandler;
    handleChangeXls: ChangeHandler;
    handleChangeXml: ChangeHandler;
    handleFocusFileName: FocusHandler;
    handleBlurFileName: FocusHandler;
    handleBlurPath: FocusHandler;
    fileTypes: { [type: string]: boolean };
    exampleFileName: string;
    legendVisible: boolean;
};
type _RecomposeProps = RecomposeProps & {
    _setPath: (p: string) => void;
    _setFileNameFormat: (f: string) => void;
    _setCSVDelimType: (delim: string) => void;
    _setExampleFileName: (name: string) => void;
    _setLegendVisible: (v: boolean) => void;
};

const mapStateToProps = (state: State) => ({
    tzOffsetCalculator: getTimezoneOffsetCalculator(state),
    defaultReportsPath: getReportsPath(state),
});

const makeExampleFileName = ({ reportId, reportName, reportSettings, fileNameFormat, tzOffsetCalculator }: Props & _RecomposeProps) => {
    const format =
        reportSettings.useDefaultFileName || !fileNameFormat
            ? ReportSettings.defaultFileNameFormat
            : fileNameFormat;
    
    return generateFileName({
        format,
        extension: (reportSettings.saveFileTypes[0] || 'pdf').toLowerCase(),
        reportId: reportId || 999,
        reportName: reportName,
        utcNow: Date.now(),
        tzOffsetCalculator,
        useUtc: reportSettings.useUtcInFileName,
    });
};

// Helper function to handle common functionality of updating report settings
// and then updating the example filename in the UI
const updateReportSettingsAndExampleFileName = (props: Props & _RecomposeProps, fn?: (rs: Readonly<ReportSettings>) => Readonly<ReportSettings>) => {
    const { onChange, _setExampleFileName } = props;
    
    onChange(reportSettings => {
        const newReportSettings = fn ? fn(reportSettings) : reportSettings;

        const exampleFileName = makeExampleFileName({ ...props, reportSettings: newReportSettings });
        _setExampleFileName(exampleFileName);

        return newReportSettings;
    });
};

type Props = OwnProps & StateProps & RecomposeProps & TranslateProps & OemProps;

export const FileDistribution = compose<Props, OwnProps>(
    setDisplayName('FileDistribution'),
    translate(),
    skin,
    connect<StateProps, {}, {}>(mapStateToProps),
    withState('exampleFileName', '_setExampleFileName', makeExampleFileName),
    withState('legendVisible', '_setLegendVisible', false),
    withState('fileNameFormat', '_setFileNameFormat', ({ reportSettings }: OwnProps) =>
        reportSettings.fileNameFormat || ''
    ),
    withState('csvDelim', '_setCSVDelimType', ({ reportSettings }: OwnProps) =>
        reportSettings.FileCSVDelimType || ''
    ),
    withState('path', '_setPath', ({ reportSettings, defaultReportsPath }: OwnProps & StateProps) =>
        reportSettings.fileDestination || defaultReportsPath
    ),
    lifecycle<Props & _RecomposeProps, {}>({
        componentDidUpdate(oldProps) {
            let updated = false;

            // Update the internal filename format if a new report has loaded
            const { _setFileNameFormat, reportSettings } = this.props;
            if (reportSettings.fileNameFormat !== oldProps.reportSettings.fileNameFormat) {
                _setFileNameFormat(this.props.reportSettings.fileNameFormat);
                updated = true;
            }

            // Update the internal path if a new report has loaded
            const { _setPath, defaultReportsPath } = this.props;
            if (reportSettings.fileDestination !== oldProps.reportSettings.fileDestination) {
                _setPath(reportSettings.fileDestination || defaultReportsPath);
                updated = true;
            }

            // Update the example file name if any of its dependencies have changed
            if (this.props.reportName !== oldProps.reportName ||
                this.props.reportId !== oldProps.reportId ||
                updated) {
                updateReportSettingsAndExampleFileName(this.props);
            }
        },
    }),
    withProps(({ reportSettings }: OwnProps) => ({
        fileTypes: reportSettings.saveFileTypes.reduce((obj, fileType) => ({ ...obj, [fileType]: true }), {}),
    })),
    withHandlers<Props & _RecomposeProps, Partial<RecomposeProps>>({
        handleFocusFileName: ({ _setLegendVisible }) => (e: React.FocusEvent<HTMLInputElement>) => {
            _setLegendVisible(true);
        },
        handleChangeFileName: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const { _setFileNameFormat, _setExampleFileName } = props;

            const fileNameFormat = e.currentTarget.value;
            _setFileNameFormat(fileNameFormat);

            const exampleFileName = makeExampleFileName({ ...props, fileNameFormat });
            _setExampleFileName(exampleFileName);
        },
        handleBlurFileName: props => (e: React.FocusEvent<HTMLInputElement>) => {
            updateReportSettingsAndExampleFileName(props, reportSettings => {
                const fileNameFormat = e.currentTarget.value;
                if (fileNameFormat === reportSettings.fileNameFormat) {
                    return reportSettings;
                }
                return reportSettings.setFileNameFormat(fileNameFormat);
            });
            props._setLegendVisible(false);
        },
        handleChangePath: ({ _setPath }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setPath(e.currentTarget.value);
        },
        handleBlurPath: ({ onChange, path }) => (e: React.FocusEvent<HTMLInputElement>) => {
            onChange(reportSettings =>
                path === reportSettings.fileDestination
                    ? reportSettings
                    : reportSettings.setFileDestination(e.currentTarget.value)
            );
        },
        handleChangeUseCustom: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            updateReportSettingsAndExampleFileName(props, reportSettings =>
                reportSettings.setUseDefaultFileName(!e.currentTarget.checked)
            );
            // Move focus to the custom filename format input when the user enables a custom filename format
            if (e.currentTarget.checked) {
                setTimeout(() => {
                    const inputElement = document.getElementById('edit-report-file-name');
                    if (inputElement) {
                        inputElement.focus();
                    }
                }, 200);
            }
        },
        handleChangeCSVType: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            updateReportSettingsAndExampleFileName(props, reportSettings =>
                reportSettings.setFileCSVDelimType(e.currentTarget.value)
            );
            props._setCSVDelimType(e.currentTarget.value);

        },
        handleBlurCSV: ({ onChange, csvDelim }) => (e: React.FocusEvent<HTMLInputElement>) => {
            const newValue = e.currentTarget.value;
            onChange(reportSettings =>
                csvDelim === reportSettings.FileCSVDelimType
                    ? reportSettings
                    : reportSettings.setFileCSVDelimType(newValue)
            );
        },
        handleChangeUseUtc: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            updateReportSettingsAndExampleFileName(props, reportSettings =>
                reportSettings.setUseUtcInFileName(e.currentTarget.checked)
            );
        },
        handleChangeFileType: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const { type } = e.currentTarget.dataset;
            updateReportSettingsAndExampleFileName(props, reportSettings =>
                e.currentTarget.checked
                    ? reportSettings.addSaveFileType(type!)
                    : reportSettings.removeSaveFileType(type!)
            );
        },
    }),
)(RawFileDistribution);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/distribution/FileDistribution.tsx